/**
 * Runtime CDN loader for the viz libraries (vega-embed, ECharts,
 * deck.gl). Simpler than the cognos-plugin sibling because operate-ui
 * is a vanilla Vite SPA — no Cognos AMD/CJS shim on `window` so we
 * don't need the UMD-shadow dance.
 *
 * <p>Why CDN rather than `npm install`: each lib is multi-hundred-KB
 * to multi-MB minified; we'd pay for them in every initial page load
 * even when the user never opens a viz. CDN-loading on-demand keeps
 * the SPA bundle small and lets the browser cache the libs across
 * sessions.
 *
 * <p>Air-gapped deployments can override the CDN base via
 * `window.__DATUMCRAFT_VIZ_CDN__` (set by the operator at runtime
 * or via the bundled spec.json).
 */

/** Vega-Embed function signature — only the slim surface we use. */
export interface VegaEmbedFn {
  (
    container: HTMLElement,
    spec: Record<string, unknown>,
    options?: Record<string, unknown>,
  ): Promise<{ view: { finalize?: () => void } }>;
}

/** ECharts API — slim subset of the global `echarts` namespace. */
export interface EChartsApi {
  init(
    container: HTMLElement,
    theme?: string | object | null,
    opts?: { renderer?: "canvas" | "svg"; devicePixelRatio?: number; width?: number; height?: number },
  ): {
    setOption(option: Record<string, unknown>, notMerge?: boolean): void;
    resize(): void;
    dispose(): void;
  };
  dispose(instance: unknown): void;
}

export interface DeckLayerCtor {
  new (config: Record<string, unknown>): unknown;
}
/**
 * View constructors mirror the layer pattern — the standalone deck.gl
 * bundle exposes them at the top level alongside the layers. Listed
 * here so {@code DeckGlRenderer} can instantiate them by name when a
 * spec carries a {@code views} array (graph / non-geographic scenes).
 */
export interface DeckViewCtor {
  new (config: Record<string, unknown>): unknown;
}
export interface DeckApi {
  Deck: new (config: Record<string, unknown>) => {
    setProps(config: Record<string, unknown>): void;
    finalize(): void;
  };
  ScatterplotLayer: DeckLayerCtor;
  HexagonLayer:     DeckLayerCtor;
  GridLayer:        DeckLayerCtor;
  GeoJsonLayer:     DeckLayerCtor;
  ColumnLayer:      DeckLayerCtor;
  ArcLayer:         DeckLayerCtor;
  /** Used by graph / lineage scenes for edge geometry. */
  LineLayer:        DeckLayerCtor;
  /** Used by graph / lineage scenes for node labels. */
  TextLayer:        DeckLayerCtor;
  /** Non-geographic 2D view; required for graph layouts. */
  OrthographicView: DeckViewCtor;
}

const CDN_DEFAULT_BASE = "https://cdn.jsdelivr.net/npm";

const SCRIPTS = {
  // Vega chain — strict load order required (see comment in loadVegaEmbed).
  vega:      `${CDN_DEFAULT_BASE}/vega@5`,
  vegaLite:  `${CDN_DEFAULT_BASE}/vega-lite@5`,
  vegaEmbed: `${CDN_DEFAULT_BASE}/vega-embed@6`,
  echarts:   `${CDN_DEFAULT_BASE}/echarts@5/dist/echarts.min.js`,
  deckGl:    `${CDN_DEFAULT_BASE}/deck.gl@9/dist.min.js`,
  // Mermaid powers the ERD diagram for renderDataModuleSchema. v10 is
  // the current LTS line with stable erDiagram syntax + the
  // initialize/run two-step API the renderer below relies on.
  mermaid:   `${CDN_DEFAULT_BASE}/mermaid@10/dist/mermaid.min.js`,
} as const;

const pending = new Map<string, Promise<unknown>>();

function cdnBase(): string {
  if (typeof window !== "undefined") {
    const w = window as unknown as { __DATUMCRAFT_VIZ_CDN__?: string };
    if (typeof w.__DATUMCRAFT_VIZ_CDN__ === "string" && w.__DATUMCRAFT_VIZ_CDN__) {
      return w.__DATUMCRAFT_VIZ_CDN__.replace(/\/$/, "");
    }
  }
  return CDN_DEFAULT_BASE;
}

function rebase(url: string): string {
  const base = cdnBase();
  if (base === CDN_DEFAULT_BASE) return url;
  return url.replace(CDN_DEFAULT_BASE, base);
}

function inject(url: string, globalKey: string): Promise<unknown> {
  const existing = pending.get(url);
  if (existing) return existing;

  const win = window as unknown as Record<string, unknown>;
  if (typeof win[globalKey] !== "undefined") {
    const ready = Promise.resolve(win[globalKey]);
    pending.set(url, ready);
    return ready;
  }

  const promise = new Promise<unknown>((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error(`Cannot inject ${url}: document is unavailable`));
      return;
    }
    const script = document.createElement("script");
    script.src = rebase(url);
    script.async = true;
    script.onload = () => {
      const w = window as unknown as Record<string, unknown>;
      if (typeof w[globalKey] === "undefined") {
        reject(new Error(`${url} loaded but window.${globalKey} is undefined`));
        return;
      }
      resolve(w[globalKey]);
    };
    script.onerror = () => {
      reject(new Error(
        `Failed to load ${url} — check CDN reachability or set window.__DATUMCRAFT_VIZ_CDN__ for an internal mirror`,
      ));
    };
    document.head.appendChild(script);
  });
  pending.set(url, promise);
  return promise;
}

/**
 * Vega-Embed needs vega + vega-lite globals present BEFORE vega-embed
 * evaluates. Strict sequential load (NOT Promise.all) — vega-lite
 * reads `window.vega.logger` at module-init; vega-embed reads
 * `window.vegaLite.compile` at module-init. Parallelising can lose
 * either initialization on a slow CDN.
 */
export async function loadVegaEmbed(): Promise<VegaEmbedFn> {
  await inject(SCRIPTS.vega,     "vega");
  await inject(SCRIPTS.vegaLite, "vegaLite");
  return inject(SCRIPTS.vegaEmbed, "vegaEmbed") as Promise<VegaEmbedFn>;
}

export function loadECharts(): Promise<EChartsApi> {
  return inject(SCRIPTS.echarts, "echarts") as Promise<EChartsApi>;
}

export function loadDeckGl(): Promise<DeckApi> {
  return inject(SCRIPTS.deckGl, "deck") as Promise<DeckApi>;
}

/**
 * The slim Mermaid surface we use — initialize once (idempotent),
 * then render(id, text) for each diagram. `render` resolves with the
 * rendered SVG markup as a string, which we drop into the container's
 * innerHTML. Mermaid manages its own SVG layout / sizing.
 */
export interface MermaidApi {
  initialize(config: {
    startOnLoad?: boolean;
    theme?: "default" | "neutral" | "dark" | "forest" | string;
    securityLevel?: "strict" | "loose" | "antiscript" | "sandbox";
    er?: { useMaxWidth?: boolean };
    flowchart?: { useMaxWidth?: boolean };
    [k: string]: unknown;
  }): void;
  render(id: string, text: string): Promise<{ svg: string; bindFunctions?: (el: Element) => void }>;
}

export function loadMermaid(): Promise<MermaidApi> {
  return inject(SCRIPTS.mermaid, "mermaid") as Promise<MermaidApi>;
}
